import ary from "lodash/ary";
import lodashDefer from "lodash/defer";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import negate from "lodash/negate";
import { ComponentType, memo, useState } from "react";
import { ActionHandler, SelectOption } from "types";
import useOnce from "../../hooks/use-once";
import sessionStore from "../../store/session-context";

export class OperationCancelled extends Error {
	constructor() {
		super();

		this.name = "OperationCancelled";
	}
}

export class InvalidTokenError extends Error {
	constructor(message: string) {
		super(message);

		this.name = 'InvalidTokenError';
	}
}

export const hasElements = (target: any): target is any[] => isArray(target) && notEmpty(target);

export const addItem = <T>(target: T[], item: T) => [...target, item];

export const removeItem = <T>(target: T[], item: T) => target.filter(x => x !== item);

export const notEmpty = negate(isEmpty);

export const empty = (): void => void (0);

export const nullary = (f: (...args: any[]) => any) => ary(f, 0);

export const tap = <T>(action: ActionHandler<T>) => (value: T) => {
	action(value);
	return value;
};

export const apply = <T extends any>(f: (param: T) => void, param: T) => () => f(param);

export const defer = <T>(f: (param: T) => void, param: T) => () => {
	lodashDefer(f, param);
};

export const continueOrElse = (error: string) => (value: boolean) => value ? Promise.resolve() : Promise.reject(error);

export const continueWhen = <T>(predicate: (target: T) => boolean) => (value: T) => predicate(value) ? Promise.resolve(value) : Promise.reject(new OperationCancelled());

export const conditionalChaining = (value: boolean) => (value) ? Promise.resolve() : Promise.reject(new OperationCancelled());

export const merge = <T, U>(obj1: T) => (obj2: U): T & U => ({ ...obj1, ...obj2 });

export const omit = <T extends any>(...keys: string[]) => (obj: T) => {
	let newObj: any = {};
	for (const property in obj) {
		if (!keys.includes(property)) {
			newObj[property] = obj[property];
		}
	}
	return newObj;
};

export const map = <T extends any, U extends any>(mapper: (obj: T, index: number) => U) => (value: T[]) => value.map(mapper);

export const notNil = <T extends any>(value: T | null | undefined): value is T => !isNil(value);

export const staticMemo = <T extends ComponentType<any>>(component: T) => memo(component, () => true);

export const fromResult = <T>(result: T) => Promise.resolve(result);

export const fromAction = (fn?: Function) => {
	fn?.();
	return Promise.resolve();
};

export const handleError = (outputError: (message: string) => Promise<void>) => (error: any) => {
	if (error instanceof OperationCancelled) return;
	const errorMessage = error?.message ?? error?.toString();
	outputError(errorMessage)
		.then(() => {
			if (error instanceof InvalidTokenError) {
				sessionStore.getState().signout();
			}
		})
		.catch(empty);
};

export const numberFormat = (num: number) =>
	num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

export const currencyFormat = (num?: number) =>
	notNil(num) ? `$${num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}` : "";

export const toSelectOption = (value: number | string, label?: number | string): SelectOption =>
	({ value: value.toString(), label: label?.toString() ?? value.toString() });

export const useFetch = <Params extends any[], U, T extends (...args: Params) => Promise<U>>(fn: T, initialState: U, ...args: Params) => {
	const [state, setState] = useState<U>(initialState);

	const fetch = (...args: Params) => {
		fn(...args)
			.then(setState)
			.catch(handleError);
	};

	useOnce(() => {
		fetch(...args);
	});

	return [state];
};

//const buscarComercios = (a: number) => Promise.resolve(a);
//const fetch2 = <U extends any, Params extends any[]>(fn: (...args: Params[]) => Promise<U>, ...args: Params) => fn;
//const a = fetch2(buscarComercios, "6");